import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, Stack } from '@mui/material';

interface Props {
  children: ReactNode;
  /** Clears the error when it changes (e.g. the route path), so navigating away recovers. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 720, mx: 'auto' }}>
        <Alert severity="error" variant="outlined">
          <AlertTitle>Something went wrong on this page</AlertTitle>
          {this.state.error.message || 'Unexpected error'}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button size="small" variant="contained" color="error" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
            <Button size="small" variant="outlined" color="inherit" onClick={() => window.location.reload()}>
              Reload
            </Button>
            <Button size="small" color="inherit" onClick={() => { window.location.href = '/'; }}>
              Go to dashboard
            </Button>
          </Stack>
        </Alert>
      </Box>
    );
  }
}
